import type {
  UpdateLineItemResponse,
  UpdateLineItemsRequestPayload,
  UpdateLineItemsResponse,
} from '@vy/api-definition';
import { UpdateLineItemResponseSchema, UpdateLineItemsResponseSchema } from '@vy/api-definition';
import type { UseMutationOptions } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import { getPrivateApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';
import type { UpdateLineItemRequest } from './use-update-line-item-types/update-line-item-request';
import { invalidateInvoicesQueries } from '../../../utils/query-invalidation';

export function useUpdateLineItem(
  options?: UseMutationOptions<UpdateLineItemResponse, unknown, UpdateLineItemRequest>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ lineItemId, updateLineItemRequest }) => {
      const lineItemsApi = getPrivateApi();
      const updateLineItemResponse = await lineItemsApi.updateLineItem({
        lineItemId,
        updateLineItemRequest,
      });

      return UpdateLineItemResponseSchema.parse(updateLineItemResponse);
    },
    ...options,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(queryKeys.dynamicInvoice()),
        invalidateInvoicesQueries(queryClient),
      ]);
    },
  });
}

export function useUpdateLineItems(
  options?: UseMutationOptions<UpdateLineItemsResponse, unknown, UpdateLineItemsRequestPayload['lineItems']>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lineItems) => {
      const lineItemsApi = getPrivateApi();
      const updateLineItemsResponse = await lineItemsApi.updateLineItems({
        updateLineItemsRequest: { lineItems },
      });
      return UpdateLineItemsResponseSchema.parse(updateLineItemsResponse);
    },
    ...options,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(queryKeys.dynamicInvoice()),
        invalidateInvoicesQueries(queryClient),
      ]);
    },
  });
}
